import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  AutomationActionTemplate,
  AutomationActionType,
  BatchMode,
  ContentTemplate,
  ContentType,
  ExperienceAnchorType,
  ExperienceTemplateMessage,
  ExperienceTemplateStatus,
  getSubTaskTemplates,
  IAutomationActionTemplateEnrollInExperience,
  IAutomationTemplate,
  IExperienceTemplate,
  IExperienceTemplateResource,
  isEventDrivenTiming,
  ISupportingActor,
  ITaskTemplate,
  ITimelineItem,
  PARTICIPANT_ACTOR_KEY,
  SupportingActorPropagationScope
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { ScheduleChannelType } from '@dadohr/shared/domainTypes/schedules';
import { css, cx } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Menu,
  Popover,
  Slide,
  Tooltip,
  Typography
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import { compact, isEqual, omit, orderBy } from 'lodash';
import { CheckCircle2, ChevronUp, Eye, Mail, Zap } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import shortid from 'shortid';
import { AdditionalActionsMenu } from '../../../../../components/AdditionalActionsMenu';
import { CustomInputLabel } from '../../../../../components/CustomInputLabel';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../../components/DialogActionsWithSlots';
import { DialogContentWithoutPadding } from '../../../../../components/DialogContentWithoutPadding';
import { DiscardChangesDialog } from '../../../../../components/DiscardChangesDialog';
import { InfoBox } from '../../../../../components/InfoBox';
import { TokenDefinition } from '../../../../../components/MarkdownText';
import { Timeline, TimelineItem } from '../../../../../components/Timeline';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useTrackDuration } from '../../../../../hooks/useTrackDuration';
import { useUnsafeTranslation } from '../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import {
  SubHeadlineContainer,
  SubHeadLineItem
} from '../../../../../layout/SubHeadlineContainer';
import { useRoutes, useStringQueryParam } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { debuggedShallowEqual } from '../../../../../services/debug';
import { useNewUx } from '../../../../../services/newUx';
import { getEmptyAutomationTemplate } from '../../../../../services/experiences/automation';
import { prepareTaskDuplicationResourceMap } from '../../../../../services/experiences/bgCheckResourceDuplication';
import { getEmptyTaskTemplate } from '../../../../../services/experiences/task';
import {
  duplicateAutomationTemplate,
  duplicateMessageTemplate,
  duplicateTaskTemplate,
  getAllMessageTemplates,
  getDirectEffectChildren,
  getExperienceTemplateById,
  getMessageBatchConnections,
  getTemplateDuration,
  getTriggerConnectors,
  getTriggeredItemsForTask,
  getTriggerHierarchyByEffect,
  useTriggeredItemsForTask
} from '../../../../../services/experiences/template';
import {
  getFixedTimingOrApproximateParentTiming,
  getTimelinePositionForAutomationTemplate,
  getTimelinePositionForMessageTempate,
  getTimelinePositionForTaskTemplate,
  getTimingValidationIssue,
  isValidTiming
} from '../../../../../services/experiences/timing';
import { updateDataInDoc } from '../../../../../services/firebase';
import { useProductAnalytics } from '../../../../../services/productAnalytics';
import { interpolateExperienceText } from '../../../../../services/set/interpolation';
import { humanizeDuration, ONE_DAY } from '../../../../../services/time';
import { getWeekLabel } from '../../../../../services/experiences/weekLabels';
import { useTheme } from '../../../../../themes';
import {
  ACTOR_COLORS_LIGHT,
  ActorDescriptor,
  ActorLegend,
  ActorLegendV2,
  useActorDescriptorsFromTemplate
} from '../../../components/Actors';
import {
  getTemplateAudienceCarriers,
  getVisibleItemIdsForSegment
} from '../../../components/ExperienceTimeline/segmentFilter';
import {
  SegmentFilterRow,
  useTemplateSegments
} from '../../../components/Segments';
import {
  AnchorField,
  useAllRelevantAnchorRulers,
  useAnchorFields
} from '../../../components/Anchors';
import {
  AutomationBuilder,
  AutomationBuilderReadOnly
} from '../../../components/Builders/AutomationBuilder';
import {
  AutomationBuilderActionValidationState,
  EnrollInExperienceValidationContext,
  hasValidActionState,
  validateActions
} from '../../../components/Builders/AutomationBuilder/validation';
import {
  TaskBuilder,
  TaskBuilderReadOnly
} from '../../../components/Builders/TaskBuilder';
import { useTokenDefinitionsForExperience } from '../../../components/Builders/TaskBuilder/components/BlockSettings/common';
import {
  TaskBuilderContext,
  useTaskBuilderContext
} from '../../../components/Builders/TaskBuilder/types';
import {
  hasValidBlockState,
  TaskBuilderBlockValidationState,
  validateBlocks
} from '../../../components/Builders/TaskBuilder/validation';
import { DeactivateItemButton } from '../../../components/DeactivateItemButton';
import { GoToTimelineElementEditButton } from '../../../components/GoToTimelineElementEditButton';
import { SELECTED_BLOCK_QUERY_PARAM } from '../../../components/GoToTimelineElementEditButton/selectedBlockParam';
import { TemplateManageActorsButton } from '../../../components/ManageActorsDrawer';
import {
  MessageItem2,
  MessageTemplateDialog,
  updateMessages
} from '../../../components/Messages';
import {
  TaskTimelineZoomControls,
  TaskTimelineZoomControlsContainer,
  useTaskTimelineScaleChangeRequest
} from '../../../components/TaskTimelineZoomControls';
import { TemplateSearchButton } from '../../../components/TemplateSearchDrawer';
import { getItemDisplayName } from '../../../helpers/adminName';
import { ExperiencePreviewV2Button } from '../../Details/ExperiencePreview';
import { SendTestTaskButton } from '../../Details/SendTest';
import { TimelineItemNotesTab } from '../../Details/TimelineItemNotesTab';
import { NewUxButton } from '../../../components/Builders/newux/kit/Button';
import {
  AutomationTimelineItem,
  sortTaskTimelineItems,
  TaskTimelineItem
} from '../../Details/TaskTimeline';
import { StandardFooter } from '../Footer';
import { StepBodyProps } from '../types';
import { ValidationBanner } from '../ValidationBanner';

const TimelineContainer = styled('div')`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

/**
 * Primary "Save/Add" action in a timeline-item dialog header. In NEW_UX it uses the
 * kit NewUxButton so it matches the participant-dialog header buttons; classic keeps
 * the app's MUI Button.
 */
export const PrimaryHeaderButton: React.FC<{
  newUx: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ newUx, disabled, onClick, children }) =>
  newUx ? (
    <NewUxButton
      variant="contained"
      color="primary"
      size="medium"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </NewUxButton>
  ) : (
    <Button
      variant="contained"
      color="primary"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );

// Automation actions carry `supportingActors` mappings into other templates
// (EnrollInExperience), and validating those mappings needs each target
// template's current supportingActors — not available synchronously to a
// field validator, so it's fetched here and threaded through as `context`.
const buildEnrollInExperienceValidationContext = async (
  actions: AutomationActionTemplate[]
): Promise<EnrollInExperienceValidationContext> => {
  const tplIds = Array.from(
    new Set(
      actions
        .filter(
          (a): a is IAutomationActionTemplateEnrollInExperience =>
            a.type === AutomationActionType.ENROLL_IN_EXPERIENCE
        )
        .map((a) => a.tplToEnrollIn)
        .filter(Boolean)
    )
  );
  const tpls = await Promise.all(
    tplIds.map((id) => getExperienceTemplateById(id))
  );
  const targetTemplatesByTplId: { [tplId: string]: IExperienceTemplate } = {};
  tplIds.forEach((id, i) => {
    const tpl = tpls[i];
    if (tpl) {
      targetTemplatesByTplId[id] = tpl.data;
    }
  });
  return { targetTemplatesByTplId };
};

export const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const AutomationEditDialog = React.memo(
  ({
    open,
    onClose,
    value,
    onChange,
    resources,
    onChangeResources,
    onDelete,
    expTpl,
    expTplId,
    customFields,
    anchorFields,
    setDuplicatedAutomation,
    onGoToOtherItem,
    isRoughDraft,
    initialSelectedActionId
  }: {
    open: boolean;
    onClose: () => void;
    value: IAutomationTemplate;
    onChange: (tpl: IAutomationTemplate) => void;
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
    onDelete: (tpl: IAutomationTemplate) => void;
    expTpl: TaskBuilderContext;
    customFields: CustomUserField[];
    anchorFields: AnchorField[];
    expTplId: string;
    setDuplicatedAutomation: React.Dispatch<
      React.SetStateAction<IAutomationTemplate | undefined>
    >;
    onGoToOtherItem?: (item: ITimelineItem) => void;
    isRoughDraft?: boolean;
    /** Action to open on mount, e.g. the one selected in the read-only dialog. */
    initialSelectedActionId?: string | null;
  }) => {
    const { org } = useCurrentUser();
    const { t } = useUnsafeTranslation();
    const { enqueueSnackbarError, enqueueSnackbarSuccess } = useSnackbar();
    const [model, setModel] = useState(value);
    const prevOpen = useRef(open);
    const { trackEvent } = useProductAnalytics();

    const [
      actionValidation,
      setActionValidation
    ] = useState<AutomationBuilderActionValidationState>(EMPTY_OBJ);

    useEffect(() => {
      if (open && !prevOpen.current) {
        setModel(value);
        setActionValidation(validateActions(value.actions, t, expTpl, value));
      }
    }, [open, value, t, expTpl]);

    const discardDialog = useDialogState(false);

    const tokens = useTokenDefinitionsForExperience(
      expTpl.supportingActors,
      org.customUserFields,
      { orgId: expTpl.orgId, experienceTemplateId: expTpl.templateId }
    );

    const otherItemRef = useRef<ITimelineItem | null>(null);

    const close = () => {
      onClose();
      if (onGoToOtherItem && otherItemRef.current) {
        onGoToOtherItem(otherItemRef.current);
        otherItemRef.current = null;
      }
    };

    const tryClose = () => {
      if (!isEqual(model, value)) {
        discardDialog.openDialog();
        return;
      }
      close();
    };

    const tryToGoToOtherItem = onGoToOtherItem
      ? (otherItem: ITimelineItem) => {
          otherItemRef.current = otherItem;
          tryClose();
        }
      : undefined;

    const trackCreateDuplicateClicked = (duplicateId: string) => {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Duplicate Timeline Item Button Clicked',
        Explanation:
          'Admin clicks the button to duplicate a timeline item, opening up a Create Timeline Item modal',
        'Experience Template ID': expTplId,
        'Original Timeline Item ID': value.id,
        'Original Timeline Item Title': value.title,
        'Button Location': 'Timeline Item Edit Modal',
        'Duplicate Timeline Item ID': duplicateId,
        'Timeline Item Type': 'Automation'
      });
    };

    const newUxEnabled = useNewUx().enabled;
    const doDuplicateAutomation = () => {
      const duplicate = duplicateAutomationTemplate(model);
      setDuplicatedAutomation({
        ...duplicate,
        title: `Copy of ${duplicate.title}`
      });
      trackCreateDuplicateClicked(duplicate.id);
      setTimeout(() => close(), 300);
    };
    const saveButton = (
      <PrimaryHeaderButton
        newUx={newUxEnabled}
        disabled={!model.actions.length || !model.title}
        onClick={async () => {
          if (!isRoughDraft) {
            if (!isValidTiming(model.timing)) {
              enqueueSnackbarError(t('Your start settings are invalid. Please review.'));
              return;
            }
            const context = await buildEnrollInExperienceValidationContext(
              model.actions
            );
            const nextActionValidation = validateActions(
              model.actions,
              t,
              expTpl,
              value,
              context
            );
            setActionValidation(nextActionValidation);
            if (!hasValidActionState(nextActionValidation)) {
              enqueueSnackbarError('Please review your actions');
              return;
            }
          }
          onChange(model);
          close();
          setModel(getEmptyAutomationTemplate());
        }}
      >
        {t('Save Automation')}
      </PrimaryHeaderButton>
    );
    // Toggle deactivation and persist immediately (bypassing the Save button's
    // validation — you can pause an item without it being complete). Shared by the
    // classic header button and the NEW_UX overflow menu / status pill.
    const doToggleDeactivate = () => {
      const next = { ...model, deactivated: !model.deactivated };
      setModel(next);
      onChange(next);
      enqueueSnackbarSuccess(
        next.deactivated ? 'Automation deactivated' : 'Automation reactivated'
      );
    };
    const primaryButton = (
      <>
        <DeactivateItemButton
          deactivated={model.deactivated}
          onConfirm={doToggleDeactivate}
        />
        <Button variant="outlined" onClick={doDuplicateAutomation}>
          {t('Duplicate')}
        </Button>
        {saveButton}
      </>
    );
    return (
      <>
        <Dialog open={open} onClose={tryClose} maxWidth={'xl'} fullWidth>
          {!newUxEnabled && (
            <DialogActionsHeader
              onClose={tryClose}
              left={
                <Typography variant="h4" component="h1">
                  {t('Edit Automation')}
                </Typography>
              }
            >
              {primaryButton}
            </DialogActionsHeader>
          )}
          <DialogContentWithoutPadding fullHeight>
            <AutomationBuilder
              value={model}
              onChange={setModel}
              expTpl={expTpl}
              onDelete={onDelete}
              initialSelectedActionId={initialSelectedActionId}
              validation={actionValidation}
              resources={resources}
              onChangeResources={onChangeResources}
              setValidation={setActionValidation}
              customFields={customFields}
              newUxPrimaryAction={newUxEnabled ? saveButton : undefined}
              newUxOnDuplicate={newUxEnabled ? doDuplicateAutomation : undefined}
              newUxOnToggleDeactivate={
                newUxEnabled ? doToggleDeactivate : undefined
              }
              onCloseDialog={tryClose}
              anchorFields={anchorFields}
              tokens={tokens}
              onGoToOtherItem={tryToGoToOtherItem}
              notesPanel={
                <TimelineItemNotesTab
                  orgId={expTpl.orgId}
                  experienceTemplateId={expTpl.templateId}
                  itemId={model.id}
                  itemType="AUTOMATION"
                />
              }
            />
          </DialogContentWithoutPadding>
        </Dialog>
        <DiscardChangesDialog
          open={discardDialog.dialogOpen}
          onCancel={discardDialog.closeDialog}
          onConfirm={close}
        />
      </>
    );
  }
);

const AutomationAddDialog = React.memo(
  ({
    open,
    onClose,
    onAdd,
    expTpl,
    resources,
    onChangeResources,
    params,
    customFields,
    anchorFields,
    duplicatedAutomation,
    getDuration,
    startTimer,
    onGoToOtherItem,
    isRoughDraft
  }: {
    open: boolean;
    onClose: () => void;
    onAdd: (tpl: IAutomationTemplate) => void;
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
    expTpl: TaskBuilderContext;
    params?: { start?: number };
    customFields: CustomUserField[];
    anchorFields: AnchorField[];
    duplicatedAutomation?: IAutomationTemplate;
    getDuration: () => number;
    startTimer: () => number;
    onGoToOtherItem?: (item: ITimelineItem) => void;
    isRoughDraft?: boolean;
  }) => {
    const { t } = useUnsafeTranslation();
    const { org } = useCurrentUser();
    const { trackEvent } = useProductAnalytics();
    const { enqueueSnackbarError } = useSnackbar();
    const [model, setModel] = useState(() => {
      if (duplicatedAutomation) return duplicatedAutomation;
      const tpl = getEmptyAutomationTemplate();
      // if (tpl.at.type === 'ABSOLUTE' && params && params.start !== undefined) {
      //   tpl.at.start = params.start;
      // }
      if (tpl.timing.type === 'fixed' && params && params.start !== undefined) {
        tpl.timing.start = params.start;
      }
      return tpl;
    });

    const [
      actionValidation,
      setActionValidation
    ] = useState<AutomationBuilderActionValidationState>(EMPTY_OBJ);

    useEffect(() => {
      startTimer();
    }, [startTimer]);

    const discardDialog = useDialogState(false);

    const tokens = useTokenDefinitionsForExperience(
      expTpl.supportingActors,
      org.customUserFields,
      { orgId: expTpl.orgId, experienceTemplateId: expTpl.templateId }
    );

    const otherItemRef = useRef<ITimelineItem | null>(null);

    const close = () => {
      onClose();
      if (onGoToOtherItem && otherItemRef.current) {
        onGoToOtherItem(otherItemRef.current);
        otherItemRef.current = null;
      }
    };

    const tryClose = () => {
      if (!isEqual(model, duplicatedAutomation)) {
        discardDialog.openDialog();
        return;
      }
      close();
    };

    const tryToGoToOtherItem = onGoToOtherItem
      ? (otherItem: ITimelineItem) => {
          otherItemRef.current = otherItem;
          tryClose();
        }
      : undefined;

    const trackAddAutomation = (duration: number) => {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Timeline Item Created',
        Explanation:
          'Admin creates a new timeline item, by clicking the Add button in the Edit Timeline Item modal',
        'Experience Template ID': expTpl.templateId,
        'Timeline Item ID': model.id,
        'Timeline Item Type': 'Automation',
        'Start Date':
          model.timing.type === 'fixed' ? model.timing.start / ONE_DAY + 1 : -1,
        Duplicate: duplicatedAutomation ? 'true' : 'false',
        'Creation Duration': duration
      });
    };
    const newUxEnabled = useNewUx().enabled;
    const primaryButton = (
      <PrimaryHeaderButton
        newUx={newUxEnabled}
        disabled={!model.actions.length || !model.title}
        onClick={async () => {
          if (!isRoughDraft) {
            if (!isValidTiming(model.timing)) {
              enqueueSnackbarError(t('Your start settings are invalid. Please review.'));
              return;
            }
            const context = await buildEnrollInExperienceValidationContext(
              model.actions
            );
            const nextActionValidation = validateActions(
              model.actions,
              t,
              expTpl,
              model,
              context
            );
            setActionValidation(nextActionValidation);
            if (!hasValidActionState(nextActionValidation)) {
              enqueueSnackbarError('Please review your actions');
              return;
            }
          }
          onAdd(model);
          trackAddAutomation(Math.round(getDuration() / 1000));
          close();
          setModel(getEmptyAutomationTemplate());
        }}
      >
        {t('Add Automation')}
      </PrimaryHeaderButton>
    );
    return (
      <>
        <Dialog
          open={open}
          onClose={tryClose}
          maxWidth={'xl'}
          fullWidth
          TransitionComponent={duplicatedAutomation && Transition}
          transitionDuration={300}
        >
          {!newUxEnabled && (
            <DialogActionsHeader
              onClose={tryClose}
              left={
                <Typography variant="h4" component="h1">
                  {t('Create Automation')}
                </Typography>
              }
            >
              {primaryButton}
            </DialogActionsHeader>
          )}
          <DialogContentWithoutPadding fullHeight>
            <AutomationBuilder
              value={model}
              onChange={setModel}
              expTpl={expTpl}
              resources={resources}
              onChangeResources={onChangeResources}
              validation={actionValidation}
              setValidation={setActionValidation}
              anchorFields={anchorFields}
              customFields={customFields}
              tokens={tokens}
              onGoToOtherItem={tryToGoToOtherItem}
              newUxPrimaryAction={newUxEnabled ? primaryButton : undefined}
              onCloseDialog={tryClose}
            />
          </DialogContentWithoutPadding>
        </Dialog>
        <DiscardChangesDialog
          open={discardDialog.dialogOpen}
          onCancel={discardDialog.closeDialog}
          onConfirm={close}
        />
      </>
    );
  }
);

const TaskAddDialog = React.memo(
  ({
    open,
    onClose,
    onAdd,
    expTpl,
    params,
    resources,
    onChangeResources,
    duplicatedTask,
    getDuration,
    startTimer,
    onGoToOtherItem,
    isRoughDraft
  }: {
    open: boolean;
    onClose: () => void;
    onAdd: (tpl: ITaskTemplate) => void;
    expTpl: Doc<IExperienceTemplate>;
    params?: { start?: number; end?: number };
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
    startTimer: () => number;
    getDuration: () => number;
    duplicatedTask?: ITaskTemplate;
    onGoToOtherItem?: (item: ITimelineItem) => void;
    isRoughDraft?: boolean;
  }) => {
    const { org } = useCurrentUser();
    const { t } = useTranslation();
    const { trackEvent } = useProductAnalytics();
    const { enqueueSnackbarError, enqueueSnackbarSuccess } = useSnackbar();
    const [tpl, setTpl] = useState(
      // TODO add params correctly
      duplicatedTask
        ? duplicatedTask
        : getEmptyTaskTemplate(expTpl.data.preferUserTimezone, params)
    );

    useEffect(() => {
      startTimer();
    }, [startTimer]);

    const [
      blockValidation,
      setBlockValidation
    ] = useState<TaskBuilderBlockValidationState>(EMPTY_OBJ);

    const subTasks = useMemo(() => getSubTaskTemplates(tpl), [tpl]);
    const expTplMini = useTaskBuilderContext(expTpl.data);
    const triggeredItems = useTriggeredItemsForTask(expTpl, tpl.id);

    const discardDialog = useDialogState(false);

    const tokens = useTokenDefinitionsForExperience(
      expTplMini.supportingActors,
      org.customUserFields,
      { orgId: expTplMini.orgId, experienceTemplateId: expTplMini.templateId }
    );

    const otherItemRef = useRef<ITimelineItem | null>(null);

    const close = () => {
      onClose();
      if (onGoToOtherItem && otherItemRef.current) {
        onGoToOtherItem(otherItemRef.current);
        otherItemRef.current = null;
      }
    };

    const tryClose = () => {
      if (!isEqual(tpl, duplicatedTask)) {
        discardDialog.openDialog();
        return;
      }
      close();
    };

    const tryToGoToOtherItem = onGoToOtherItem
      ? (otherItem: ITimelineItem) => {
          otherItemRef.current = otherItem;
          tryClose();
        }
      : undefined;

    const trackAddTask = (duration: number) => {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Timeline Item Created',
        Explanation:
          'Admin creates a new timeline item, by clicking the Add button in the Edit Timeline Item modal',
        'Experience Template ID': expTpl.id,
        'Timeline Item ID': tpl.id,
        'Timeline Item Type': 'Task',
        'Start Date':
          tpl.timing.type === 'fixed' ? tpl.timing.start / ONE_DAY + 1 : -1,
        Duplicate: duplicatedTask ? 'true' : 'false',
        'Creation Duration': duration
      });
    };

    const newUxEnabled = useNewUx().enabled;
    const primaryButton = (
      <PrimaryHeaderButton
        newUx={newUxEnabled}
        disabled={!subTasks.length || !tpl.title}
        onClick={() => {
          if (!isRoughDraft) {
            const timingIssue = getTimingValidationIssue(tpl.timing);
            if (timingIssue) {
              enqueueSnackbarError(
                timingIssue === 'deadline'
                  ? t('Your deadline settings are invalid. Please review.')
                  : t('Your start settings are invalid. Please review.')
              );
              return;
            }

            const nextBlockValidation = validateBlocks(tpl.blocks, t, expTplMini);
            setBlockValidation(nextBlockValidation);
            if (!hasValidBlockState(nextBlockValidation)) {
              enqueueSnackbarError('Please review your blocks');
              return;
            }
          }
          onAdd({
            ...tpl,
            adminName: tpl.adminName?.trim() || null
          });
          trackAddTask(Math.round(getDuration() / 1000));
          close();
          setTpl(getEmptyTaskTemplate(tpl.preferUserTimezone));
          enqueueSnackbarSuccess(`Added task '${tpl.title}'`);
        }}
      >
        {t('Add Task')}
      </PrimaryHeaderButton>
    );
    return (
      <>
        <Dialog
          open={open}
          onClose={tryClose}
          maxWidth={'xl'}
          fullWidth
          TransitionComponent={duplicatedTask && Transition}
          transitionDuration={300}
        >
          {!newUxEnabled && (
            <DialogActionsHeader
              onClose={tryClose}
              left={
                <Typography variant="h4" component="h1">
                  {t('Create Task')}
                </Typography>
              }
            >
              {primaryButton}
            </DialogActionsHeader>
          )}
          <DialogContentWithoutPadding fullHeight>
            <TaskBuilder
              value={tpl}
              onChange={setTpl}
              expTpl={expTplMini}
              resources={resources}
              onChangeResources={onChangeResources}
              validation={blockValidation}
              setValidation={setBlockValidation}
              sendTestTaskButton={
                <SendTestTaskButton
                  d={expTpl}
                  taskTpl={tpl}
                  disabled={!subTasks.length || !tpl.title}
                  newUx={newUxEnabled}
                />
              }
              tokens={tokens}
              triggeredItems={triggeredItems}
              onGoToOtherItem={tryToGoToOtherItem}
              newUxPrimaryAction={newUxEnabled ? primaryButton : undefined}
              onCloseDialog={tryClose}
            />
          </DialogContentWithoutPadding>
        </Dialog>

        <DiscardChangesDialog
          open={discardDialog.dialogOpen}
          onCancel={discardDialog.closeDialog}
          onConfirm={close}
        />
      </>
    );
  },
  debuggedShallowEqual('TaskAddDialog', { disable: true })
);

// TODO hover and focus style
const Element = styled('div')((p) => ({
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  maxWidth: 240,
  padding: p.theme.spacing(3),
  width: '100%',
  height: '100%',
  gap: p.theme.spacing(1)
}));

const Elements = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gridColumnGap: p.theme.spacing(2),
  gridRowGap: p.theme.spacing(3),
  width: '100%',
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(2)
}));

const ComboAddDialog = ({
  open,
  onClose,
  onTaskAdd,
  onAutomationAdd,
  onMessageAdd,
  expTpl,
  params,
  resources,
  onChangeResources,
  customFields,
  anchorFields,
  onGoToOtherItem,
  isRoughDraft
}: {
  open: boolean;
  onClose: () => void;
  onTaskAdd: (tpl: ITaskTemplate) => void;
  onAutomationAdd: (tpl: IAutomationTemplate) => void;
  onMessageAdd: (tpl: ExperienceTemplateMessage) => void;
  expTpl: Doc<IExperienceTemplate>;
  params?: { start?: number; end?: number };
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  customFields: CustomUserField[];
  anchorFields: AnchorField[];
  onGoToOtherItem?: (item: ITimelineItem) => void;
  isRoughDraft?: boolean;
}) => {
  const theme = useTheme();
  const { startTimer, getDuration } = useTrackDuration();
  const [sel, setSet] = useState<'TASK' | 'AUTOMATION' | 'MESSAGE' | null>(null);
  const close = useCallback(() => {
    setSet(null);
    onClose();
  }, [onClose]);

  const expTplMini = useTaskBuilderContext(expTpl.data);

  if (sel === 'TASK') {
    return (
      <TaskAddDialog
        open={open}
        onClose={close}
        onAdd={onTaskAdd}
        expTpl={expTpl}
        resources={resources}
        onChangeResources={onChangeResources}
        params={params || undefined}
        startTimer={startTimer}
        getDuration={getDuration}
        onGoToOtherItem={onGoToOtherItem}
        isRoughDraft={isRoughDraft}
      />
    );
  }
  if (sel === 'MESSAGE') {
    return (
      <MessageTemplateDialog
        open={open}
        onClose={close}
        expTpl={expTpl}
        value={createEmptyMessage(
          PARTICIPANT_ACTOR_KEY,
          '',
          params?.start || 0,
          expTpl.data.preferUserTimezone
        )}
        onChange={onMessageAdd}
        mode="create"
        startTimer={startTimer}
        getDuration={getDuration}
        onGoToOtherItem={onGoToOtherItem}
        isRoughDraft={isRoughDraft}
      />
    );
  }
  if (sel === 'AUTOMATION') {
    return (
      <AutomationAddDialog
        open={open}
        onClose={close}
        onAdd={onAutomationAdd}
        expTpl={expTplMini}
        params={params}
        resources={resources}
        onChangeResources={onChangeResources}
        customFields={customFields}
        anchorFields={anchorFields}
        startTimer={startTimer}
        getDuration={getDuration}
        onGoToOtherItem={onGoToOtherItem}
        isRoughDraft={isRoughDraft}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogContent>
        <Elements>
          <ButtonBase onClick={() => setSet('MESSAGE')}>
            <Element>
              <Mail size={64} color={theme.custom.colors.blue.w500} />
              <div>
                <Typography variant="h3">Message</Typography>
                <Typography variant="body1" color="textSecondary">
                  A simple message with content
                </Typography>
              </div>
            </Element>
          </ButtonBase>
          <ButtonBase onClick={() => setSet('TASK')}>
            <Element>
              <CheckCircle2 size={64} color={theme.custom.colors.yellow.w500} />
              <div>
                <Typography variant="h3">Task</Typography>
                <Typography variant="body1" color="textSecondary">
                  A task participants can complete
                </Typography>
              </div>
            </Element>
          </ButtonBase>
          <ButtonBase onClick={() => setSet('AUTOMATION')}>
            <Element>
              <Zap size={64} color={theme.custom.colors.purple.w500} />
              <div>
                <Typography variant="h3">Automation</Typography>
                <Typography variant="body1" color="textSecondary">
                  An action without user interaction
                </Typography>
              </div>
            </Element>
          </ButtonBase>
        </Elements>
      </DialogContent>
    </Dialog>
  );
};

const TaskEditDialog = ({
  open,
  onClose,
  tpl,
  onUpdate,
  onDelete,
  expTpl,
  resources,
  onChangeResources,
  setDuplicatedTask,
  onGoToOtherItem,
  isRoughDraft,
  validationErrors,
  initialSelectedBlockId
}: {
  open: boolean;
  onClose: () => void;
  tpl: ITaskTemplate;
  onUpdate: (next: ITaskTemplate) => void;
  onDelete: (t: ITaskTemplate) => void;
  expTpl: Doc<IExperienceTemplate>;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  setDuplicatedTask: React.Dispatch<
    React.SetStateAction<ITaskTemplate | undefined>
  >;
  onGoToOtherItem?: (item: ITimelineItem) => void;
  isRoughDraft?: boolean;
  validationErrors?: string[];
  /** Block to open on mount, e.g. the one selected in the read-only dialog. */
  initialSelectedBlockId?: string | null;
}) => {
  const { t } = useTranslation();
  const { org } = useCurrentUser();
  const [model, setModel] = useState(tpl);
  const prevOpen = useRef(open);
  const { enqueueSnackbarError, enqueueSnackbarSuccess } = useSnackbar();
  const expTplMini = useTaskBuilderContext(expTpl.data);
  const triggeredItems = useTriggeredItemsForTask(expTpl, tpl.id);
  const { trackEvent } = useProductAnalytics();

  const [
    blockValidation,
    setBlockValidation
  ] = useState<TaskBuilderBlockValidationState>(EMPTY_OBJ);

  // make sure that the model is fresh whenever we open
  useEffect(() => {
    if (open && !prevOpen.current) {
      setModel(tpl);
      setBlockValidation(validateBlocks(tpl.blocks, t, expTplMini));
    }
  }, [open, tpl, t, expTplMini]);

  const subTasks = useMemo(() => getSubTaskTemplates(model), [model]);
  const discardDialog = useDialogState(false);

  const tokens = useTokenDefinitionsForExperience(
    expTplMini.supportingActors,
    org.customUserFields,
    { orgId: expTplMini.orgId, experienceTemplateId: expTplMini.templateId }
  );

  const otherItemRef = useRef<ITimelineItem | null>(null);

  const close = () => {
    onClose();
    if (onGoToOtherItem && otherItemRef.current) {
      onGoToOtherItem(otherItemRef.current);
      otherItemRef.current = null;
    }
  };

  const tryClose = () => {
    if (!isEqual(model, tpl)) {
      discardDialog.openDialog();
      return;
    }
    close();
  };

  const tryToGoToOtherItem = onGoToOtherItem
    ? (otherItem: ITimelineItem) => {
        otherItemRef.current = otherItem;
        tryClose();
      }
    : undefined;

  const trackCreateDuplicateClicked = (duplicateId: string) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Duplicate Timeline Item Button Clicked',
      Explanation:
        'Admin clicks the button to duplicate a timeline item, opening up a Create Timeline Item modal',
      'Experience Template ID': expTpl.id,
      'Original Timeline Item ID': tpl.id,
      'Original Timeline Item Title': tpl.title,
      'Button Location': 'Timeline Item Edit Modal',
      'Duplicate Timeline Item ID': duplicateId,
      'Timeline Item Type': 'Task'
    });
  };

  const newUxEnabled = useNewUx().enabled;
  const doDuplicateTask = () => {
    // BG-check resources are cloned (not identity-mapped) so the
    // duplicate's package selection is independent of the original.
    // Closes main-378. Other storages stay identity-mapped — see ch9729.
    const { resourceIdToNewId, clonedBgCheckResources } =
      prepareTaskDuplicationResourceMap(model, expTpl.data.resources);
    if (clonedBgCheckResources.length) {
      onChangeResources([...expTpl.data.resources, ...clonedBgCheckResources]);
    }
    const duplicatedTask = duplicateTaskTemplate(model, resourceIdToNewId);
    setDuplicatedTask({
      ...duplicatedTask,
      title: `Copy of ${duplicatedTask.title}`
    });
    trackCreateDuplicateClicked(duplicatedTask.id);
    setTimeout(() => close(), 300);
  };
  const handleSaveTask = () => {
    if (!isRoughDraft) {
      const timingIssue = getTimingValidationIssue(model.timing);
      if (timingIssue) {
        enqueueSnackbarError(
          timingIssue === 'deadline'
            ? t('Your deadline settings are invalid. Please review.')
            : t('Your start settings are invalid. Please review.')
        );
        return;
      }
      const nextBlockValidation = validateBlocks(model.blocks, t, expTplMini);
      setBlockValidation(nextBlockValidation);
      if (!hasValidBlockState(nextBlockValidation)) {
        enqueueSnackbarError('Please review your blocks');
        return;
      }
    }
    const hasAiFeature =
      model.blocks.some((b: any) => b.aiFeatureUsed) || model.aiFeatureUsed;
    const modelToSave = {
      ...model,
      adminName: model.adminName?.trim() || null,
      ...(hasAiFeature ? { aiFeatureUsed: true } : {})
    };
    onUpdate(modelToSave);
    setModel(modelToSave); // Keep model in sync so isEqual(model, tpl) passes
    enqueueSnackbarSuccess('Saved changes');
  };
  const saveButton = (
    <PrimaryHeaderButton
      newUx={newUxEnabled}
      disabled={!subTasks.length || !model.title}
      onClick={handleSaveTask}
    >
      {t('Save Task')}
    </PrimaryHeaderButton>
  );
  // Toggle deactivation and persist immediately (bypassing the Save button's
  // content validation — you can pause an item without it being complete). Shared
  // by the classic header button and the NEW_UX overflow menu / status pill.
  const doToggleDeactivate = () => {
    const next = { ...model, deactivated: !model.deactivated };
    setModel(next);
    onUpdate(next);
    enqueueSnackbarSuccess(
      next.deactivated ? 'Task deactivated' : 'Task reactivated'
    );
  };
  const primaryActions = (
    <FlexContainer>
      <DeactivateItemButton
        deactivated={model.deactivated}
        onConfirm={doToggleDeactivate}
      />
      <Button variant="outlined" onClick={doDuplicateTask}>
        {t('Duplicate')}
      </Button>
      {saveButton}
    </FlexContainer>
  );
  return (
    <>
      <Dialog open={open} onClose={tryClose} maxWidth={'xl'} fullWidth>
        {open && (
          <>
            {!newUxEnabled && (
              <DialogActionsHeader
                left={
                  <Typography variant="h4" component="h1">
                    {t('Edit Task')}
                  </Typography>
                }
                onClose={tryClose}
              >
                {primaryActions}
              </DialogActionsHeader>
            )}
            {validationErrors && validationErrors.length > 0 && (
              <ValidationBanner errors={validationErrors} />
            )}
            <DialogContentWithoutPadding fullHeight>
              <TaskBuilder
                value={model}
                onChange={setModel}
                expTpl={expTplMini}
                resources={resources}
                onChangeResources={onChangeResources}
                validation={blockValidation}
                setValidation={setBlockValidation}
                onDelete={onDelete}
                initialSelectedBlockId={initialSelectedBlockId}
                sendTestTaskButton={
                  <SendTestTaskButton
                    d={expTpl}
                    taskTpl={model}
                    disabled={!subTasks.length || !model.title}
                    newUx={newUxEnabled}
                  />
                }
                tokens={tokens}
                triggeredItems={triggeredItems}
                onGoToOtherItem={tryToGoToOtherItem}
                newUxPrimaryAction={newUxEnabled ? saveButton : undefined}
                onDuplicate={newUxEnabled ? doDuplicateTask : undefined}
                newUxOnToggleDeactivate={
                  newUxEnabled ? doToggleDeactivate : undefined
                }
                onCloseDialog={tryClose}
                notesPanel={
                  <TimelineItemNotesTab
                    orgId={expTpl.data.orgId}
                    experienceTemplateId={expTpl.id}
                    itemId={model.id}
                    itemType="TASK"
                  />
                }
              />
            </DialogContentWithoutPadding>
          </>
        )}
      </Dialog>
      <DiscardChangesDialog
        open={discardDialog.dialogOpen}
        onCancel={discardDialog.closeDialog}
        onConfirm={close}
      />
    </>
  );
};

const TaskBuilderReadOnlyDialog = ({
  open,
  onClose,
  tpl,
  expTpl,
  resources,
  anchorFields,
  showOnCompleteHookSettings,
  onGoToOtherItem
}: {
  open: boolean;
  onClose: () => void;
  tpl: ITaskTemplate;
  expTpl: Doc<IExperienceTemplate>;
  resources: IExperienceTemplateResource[];
  anchorFields: AnchorField[];
  showOnCompleteHookSettings: boolean;
  onGoToOtherItem?: (item: ITimelineItem) => void;
}) => {
  const { org } = useCurrentUser();
  const expTplMini = useTaskBuilderContext(expTpl.data);
  const triggeredItems = useTriggeredItemsForTask(expTpl, tpl.id);
  const tokens = useTokenDefinitionsForExperience(
    expTplMini.supportingActors,
    org.customUserFields,
    { orgId: expTplMini.orgId, experienceTemplateId: expTplMini.templateId }
  );
  // Block selected in the viewer; carried into the edit URL by the Edit button.
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'xl'} fullWidth>
      {open && (
        <>
          <DialogActionsHeader onClose={onClose}>
            <GoToTimelineElementEditButton
              expTpl={expTpl}
              type="TASK"
              elementId={tpl.id}
              title={tpl.title}
              selectedBlockId={selectedBlockId}
            />
          </DialogActionsHeader>
          <DialogContentWithoutPadding fullHeight>
            <TaskBuilderReadOnly
              showOnCompleteHookSettings={showOnCompleteHookSettings}
              onSelectedBlockChange={setSelectedBlockId}
              value={tpl}
              expTpl={expTplMini}
              resources={resources}
              validation={EMPTY_OBJ}
              anchorFields={anchorFields}
              sendTestTaskButton={
                <SendTestTaskButton d={expTpl} taskTpl={tpl} />
              }
              tokens={tokens}
              triggeredItems={triggeredItems}
              onGoToOtherItem={onGoToOtherItem}
            />
          </DialogContentWithoutPadding>
        </>
      )}
    </Dialog>
  );
};

const AutomationBuilderReadOnlyDialog = ({
  open,
  onClose,
  tpl,
  expTpl,
  anchorFields,
  tokens,
  resources
}: {
  open: boolean;
  onClose: () => void;
  tpl: IAutomationTemplate;
  expTpl: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  resources: IExperienceTemplateResource[];
}) => {
  const expTplMini = useTaskBuilderContext(expTpl.data);
  // Action selected in the viewer; carried into the edit URL by the Edit button.
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  return (
    <Dialog open={open} onClose={onClose} maxWidth={'xl'} fullWidth>
      {open && (
        <>
          <DialogActionsHeader onClose={onClose}>
            <GoToTimelineElementEditButton
              expTpl={expTpl}
              type="AUTOMATION"
              elementId={tpl.id}
              title={tpl.title}
              selectedBlockId={selectedActionId}
            />
          </DialogActionsHeader>
          <DialogContentWithoutPadding fullHeight>
            <AutomationBuilderReadOnly
              value={tpl}
              expTpl={expTplMini}
              onSelectedActionChange={setSelectedActionId}
              anchorFields={anchorFields}
              validation={EMPTY_OBJ}
              tokens={tokens}
              resources={resources}
            />
          </DialogContentWithoutPadding>
        </>
      )}
    </Dialog>
  );
};

const updateTasks = (
  d: Doc<IExperienceTemplate>,
  tasks: IExperienceTemplate['tasks']
) => {
  const actors = Object.keys(d.data.supportingActors);
  return updateDataInDoc(d, {
    tasks: orderBy(tasks, [
      (t) => getFixedTimingOrApproximateParentTiming(t.timing, tasks).start,
      (t) =>
        t.actor === PARTICIPANT_ACTOR_KEY ? -Infinity : actors.indexOf(t.actor)
    ])
  });
};

const updateAutomations = (
  d: Doc<IExperienceTemplate>,
  automations: IExperienceTemplate['automations']
) => {
  return updateDataInDoc(d, {
    automations: orderBy(automations, [
      (a) => {
        const timing = getFixedTimingOrApproximateParentTiming(
          a.timing,
          d.data.tasks
        );
        return timing.start;
      }
    ])
  });
};

const updateResources = (
  d: Doc<IExperienceTemplate>,
  resources: IExperienceTemplate['resources']
) => {
  return updateDataInDoc(d, { resources });
};

// The scope chosen for an actor's edit is kept per actor on the template and
// committed by the same write as the edit; the backend reads it when the
// template changes
const updateSupportingActors = (
  d: Doc<IExperienceTemplate>,
  supportingActors: { [key: string]: ISupportingActor },
  propagationScope?: {
    actorKey: string;
    scope: SupportingActorPropagationScope;
  }
) => {
  if (!propagationScope) {
    return updateDataInDoc(d, { supportingActors });
  }
  return updateDataInDoc(d, {
    supportingActors,
    lastActorPropagationScope: {
      ...d.data.lastActorPropagationScope,
      [propagationScope.actorKey]: propagationScope.scope
    }
  });
};

export const StepTimelinePreview = ({ d }: { d: Doc<IExperienceTemplate> }) => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const newUxEnabled = useNewUx().enabled;
  const { startTimer, getDuration } = useTrackDuration();
  const anchorFields = useAnchorFields(org.customUserFields);
  const { tasks, resources, anchor, tz } = d.data;
  const orgDateFormat = org.dateFormats.MMDDYYYY;
  // Stable so the memoised header doesn't re-render on unrelated state changes.
  const renderOuterUnit = useCallback(
    (i: number, gridOffset: number) =>
      newUxEnabled
        ? getWeekLabel(
            anchor,
            tz,
            orgDateFormat,
            i * 7 + Math.round(gridOffset / ONE_DAY),
            ut
          ).full
        : `${t('Week')} ${i + 1}`,
    [newUxEnabled, anchor, tz, orgDateFormat, ut, t]
  );
  const [scale, setScale] = useState(1);
  const actorDescriptors = useActorDescriptorsFromTemplate(
    d.data.supportingActors
  );
  const expTpl = useTaskBuilderContext(d.data);

  const [previewedTaskId, setPreviewedTaskId] = useStringQueryParam('t', '');
  const previewedTask = useMemo(
    () => d.data.tasks.find((tsk) => tsk.id === previewedTaskId),
    [previewedTaskId, d]
  );
  const closeTaskPreviewDialog = useCallback(() => setPreviewedTaskId(''), [
    setPreviewedTaskId
  ]);

  const [previewedAutomationId, setPreviewedAutomationId] = useStringQueryParam(
    'a',
    ''
  );
  const previewedAutomation = useMemo(
    () => d.data.automations.find((tsk) => tsk.id === previewedAutomationId),
    [previewedAutomationId, d]
  );
  const closeAutomationPreviewDialog = useCallback(
    () => setPreviewedAutomationId(''),
    [setPreviewedAutomationId]
  );

  const [previewedMessageId, setPreviewedMessageId] = useStringQueryParam(
    'm',
    ''
  );
  const previewedMessage = useMemo(
    () => d.data.messages.other.find((msg) => msg.id === previewedMessageId),
    [previewedMessageId, d]
  );
  const closeMessagePreviewDialog = useCallback(
    () => setPreviewedMessageId(''),
    [setPreviewedMessageId]
  );

  const onGoToOtherItem = useCallback(
    (otherItem: ITimelineItem) => {
      if (otherItem.itemType === 'AUTOMATION') {
        setPreviewedAutomationId(otherItem.item.id);
      }
      if (otherItem.itemType === 'MSG') {
        setPreviewedMessageId(otherItem.item.id);
      }
      if (otherItem.itemType === 'TASK') {
        setPreviewedTaskId(otherItem.item.id);
      }
    },
    [setPreviewedAutomationId, setPreviewedMessageId, setPreviewedTaskId]
  );

  const items = useMemo(() => {
    const allMessages = getAllMessageTemplates(d.data.messages);
    const { connectUpIds, connectDownIds } = getMessageBatchConnections(d);
    const triggerHierarchyByEffects = getTriggerHierarchyByEffect(d);
    const triggerHierarchyByCause = getDirectEffectChildren(d);
    const triggerConnectors = getTriggerConnectors(d);

    return sortTaskTimelineItems(
      compact([
        ...allMessages.map<TimelineItem>((msg) => {
          const causeItems = triggerHierarchyByEffects.messages[msg.id] || [];
          const { start, end } = getTimelinePositionForMessageTempate(
            msg.timing,
            d.data.tasks
          );
          return {
            key: msg.id,
            data: {
              type: 'MESSAGE',
              actor: msg.actor,
              batchMode: msg.batchMode
            },
            start,
            end,
            parents: causeItems,
            children: triggerHierarchyByCause[msg.id] || [],
            body: (
              <MessageItem2
                tplContext={d.data}
                message={msg}
                actor={actorDescriptors[msg.actor]}
                onClick={() => setPreviewedMessageId(msg.id)}
                connectDown={connectDownIds.has(msg.id)}
                connectLeft={triggerConnectors[msg.id]?.left}
                connectRight={triggerConnectors[msg.id]?.right}
              />
            )
          };
        }),
        ...d.data.tasks.map<TimelineItem>((tpl) => {
          const causeItems = triggerHierarchyByEffects.tasks[tpl.id] || [];
          const { start, end } = getTimelinePositionForTaskTemplate(
            tpl.timing,
            d.data.tasks
          );
          return {
            key: tpl.id,
            start,
            end,
            data: { type: 'TASK', actor: tpl.actor, batchMode: tpl.batchMode },
            parents: causeItems,
            children: triggerHierarchyByCause[tpl.id] || [],
            body: (
              <TaskTimelineItem
                title={interpolateExperienceText(getItemDisplayName(tpl), {
                  experience: { vars: { title: d.data.title, tasks: {} } },
                  org: { vars: { name: org.name } },
                  roles: []
                }, { dateFormat: 'YYYY/MM/DD' })}
                channels={tpl.channels}
                actor={actorDescriptors[tpl.actor]}
                deactivated={tpl.deactivated}
                onClick={() => setPreviewedTaskId(tpl.id)}
                connectUp={connectUpIds.has(tpl.id)}
                connectDown={connectDownIds.has(tpl.id)}
                connectLeft={triggerConnectors[tpl.id]?.left}
                connectRight={triggerConnectors[tpl.id]?.right}
              />
            )
          };
        }),
        ...d.data.automations.map<TimelineItem | null>((tpl) => {
          const causeItems =
            triggerHierarchyByEffects.automations[tpl.id] || [];
          const { start, end } = getTimelinePositionForAutomationTemplate(
            tpl.timing,
            d.data.tasks
          );
          return {
            key: tpl.id,
            start,
            end,
            data: { type: 'AUTOMATION' },
            parents: causeItems,
            children: triggerHierarchyByCause[tpl.id] || [],
            body: (
              <AutomationTimelineItem
                tpl={tpl}
                onClick={() => setPreviewedAutomationId(tpl.id)}
                connectLeft={triggerConnectors[tpl.id]?.left}
                connectRight={triggerConnectors[tpl.id]?.right}
              />
            )
          };
        })
      ])
    );
  }, [
    d,
    actorDescriptors,
    setPreviewedTaskId,
    setPreviewedAutomationId,
    setPreviewedMessageId
  ]);

  const expectedEnd =
    items.length > 0
      ? Math.max(...items.map((i) => i.end)) + d.data.expectedEndThreshold
      : 0;

  const anchorRulers = useAllRelevantAnchorRulers(
    anchor,
    anchorFields,
    t,
    expectedEnd,
    items.length > 0
  );

  const duration = getTemplateDuration(d);
  const onScaleChangeRequest = useTaskTimelineScaleChangeRequest(setScale);
  const hasZoomControls = duration > ONE_DAY * 21;

  const tokens = useTokenDefinitionsForExperience(
    expTpl.supportingActors,
    org.customUserFields,
    { orgId: expTpl.orgId, experienceTemplateId: expTpl.templateId }
  );

  return (
    <>
      <SubHeadlineContainer>
        <SubHeadLineItem>
          {t('taskWithCount', { count: tasks.length })}{' '}
        </SubHeadLineItem>
        <SubHeadLineItem>
          {t('totalDuration')}:{' '}
          {humanizeDuration(duration + d.data.expectedEndThreshold)}
        </SubHeadLineItem>
      </SubHeadlineContainer>
      <FlexContainer spacing={3}>
        <ActorLegend descriptors={actorDescriptors} />
      </FlexContainer>
      <TimelineContainer>
        <Timeline
          items={items}
          innerUnitSpan={ONE_DAY * 1}
          outerUnitSpan={ONE_DAY * 7}
          renderOuterUnit={renderOuterUnit}
          scale={scale}
          onScaleChangeRequest={
            hasZoomControls ? onScaleChangeRequest : undefined
          }
          rulers={anchorRulers}
        />
        {hasZoomControls && (
          <TaskTimelineZoomControlsContainer stickyOffset={100}>
            <TaskTimelineZoomControls value={scale} onChange={setScale} />
          </TaskTimelineZoomControlsContainer>
        )}
      </TimelineContainer>

      {previewedTask && (
        <TaskBuilderReadOnlyDialog
          open={true}
          onClose={closeTaskPreviewDialog}
          tpl={previewedTask}
          expTpl={d}
          resources={resources}
          anchorFields={anchorFields}
          showOnCompleteHookSettings={true}
          onGoToOtherItem={onGoToOtherItem}
        />
      )}
      {previewedAutomation && (
        <AutomationBuilderReadOnlyDialog
          open={true}
          onClose={closeAutomationPreviewDialog}
          tpl={previewedAutomation}
          expTpl={d}
          anchorFields={anchorFields}
          tokens={tokens}
          resources={resources}
        />
      )}
      {previewedMessage && (
        <MessageTemplateDialog
          open={true}
          onClose={closeMessagePreviewDialog}
          value={previewedMessage}
          expTpl={d}
          mode="read"
          startTimer={startTimer}
          getDuration={getDuration}
        />
      )}
    </>
  );
};

const useTimelineItems = (
  d: Doc<IExperienceTemplate>,
  actorDescriptors: { [key: string]: ActorDescriptor },
  onTaskClick: (taskId: string) => void,
  onAutomationClick: (automationId: string) => void,
  onMessageClick: (messageId: string) => void,
  setDuplicatedTask: React.Dispatch<
    React.SetStateAction<ITaskTemplate | undefined>
  >,
  setDuplicatedAutomation: React.Dispatch<
    React.SetStateAction<IAutomationTemplate | undefined>
  >,
  setDuplicatedMessage: React.Dispatch<
    React.SetStateAction<ExperienceTemplateMessage | undefined>
  >,
  // Needed by the per-task "Duplicate" action below to register cloned
  // BG-check resources on the template. See main-378.
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void
): TimelineItem[] => {
  const { trackEvent } = useProductAnalytics();
  const { org } = useCurrentUser();

  const prevTaskItemsRef = useRef<{
    [key: string]: {
      tpl: ITaskTemplate;
      item: TimelineItem;
    };
  }>({});
  const prevAutomationItemsRef = useRef<{
    [key: string]: {
      tpl: IAutomationTemplate;
      item: TimelineItem;
    };
  }>({});

  const prevMsgItemsRef = useRef<{
    [key: string]: {
      tpl: ExperienceTemplateMessage;
      item: TimelineItem;
    };
  }>({});

  const prevArgsRef = useRef({
    actorDescriptors
  });

  // TODO - add automation timeline items
  // - start with absolutely positioned ones only
  // - sames rules as for tasks apply in terms of updating them etc
  // - the problematic piece is how wide to draw them as they are
  // - technically points in time.
  // - they need to have a fixed width, so that they can be moved around
  //
  // - resizing poses a problem - when they are absolute we want them to be moveable as well
  // - this means that we need to start differentiating between what kind of item we are moving.
  // - the key needs to become more complex and reveal information about its type

  return useMemo(() => {
    const prevArgs = prevArgsRef.current;
    const prevTaskItems = prevTaskItemsRef.current;
    const prevAutomationItems = prevAutomationItemsRef.current;
    const prevMsgItems = prevMsgItemsRef.current;

    const forceNew = prevArgs.actorDescriptors !== actorDescriptors;
    // const forceNew = true;

    const items: TimelineItem[] = [];

    const allMessages = getAllMessageTemplates(d.data.messages);

    const { connectUpIds, connectDownIds } = getMessageBatchConnections(d);
    const triggerHierarchyByEffects = getTriggerHierarchyByEffect(d);
    const triggerConnectors = getTriggerConnectors(d);
    console.log('trigger connectors', triggerConnectors);

    const trackDuplicateButtonClicked = (
      duplicateId: string,
      originalId: string,
      originalItemTitle: string,
      timelineItemType: 'Message' | 'Task' | 'Automation'
    ) => {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Duplicate Timeline Item Button Clicked',
        Explanation:
          'Admin clicks the button to duplicate a timeline item, opening up a Create Timeline Item modal',
        'Experience Template ID': d.id,
        'Original Timeline Item ID': originalId,
        'Original Timeline Item Title': originalItemTitle,
        'Button Location': 'Timeline',
        'Duplicate Timeline Item ID': duplicateId,
        'Timeline Item Type': timelineItemType
      });
    };

    allMessages.forEach((msg, i) => {
      const options = [
        {
          key: 'duplicateTimelineItemMessage',
          label: 'Duplicate',
          onClick: () => {
            const duplicate = duplicateMessageTemplate(msg);
            setDuplicatedMessage({
              ...duplicate,
              subject: `Copy of ${duplicate.subject}`
            });
            trackDuplicateButtonClicked(
              duplicate.id,
              msg.id,
              msg.subject,
              'Message'
            );
          }
        }
      ];
      // TODO Triggered items - these optimizations are a bit on the way of dealing properly with triggering and triggered
      // items. Once we also make triggered items moveable this will need more changes.
      if (
        forceNew ||
        prevMsgItems[msg.id]?.tpl !== msg ||
        isEventDrivenTiming(msg.timing)
      ) {
        const causeItems = triggerHierarchyByEffects.messages[msg.id] || [];
        const { start, end } = getTimelinePositionForMessageTempate(
          msg.timing,
          d.data.tasks
        );
        const nextItem: TimelineItem = {
          key: msg.id,
          data: { type: 'MESSAGE', actor: msg.actor, batchMode: msg.batchMode },
          start,
          end,
          movable: msg.timing.type === 'fixed',
          parents: causeItems,
          body: (
            <MessageItem2
              tplContext={d.data}
              message={msg}
              variant="normal"
              actor={actorDescriptors[msg.actor]}
              onClick={() => onMessageClick(msg.id)}
              selectMenu={
                <AdditionalActionsMenu options={options} variant="horizontal" />
              }
              connectDown={connectDownIds.has(msg.id)}
              connectLeft={triggerConnectors[msg.id]?.left}
              connectRight={triggerConnectors[msg.id]?.right}
            />
          )
        };
        prevMsgItems[msg.id] = { tpl: msg, item: nextItem };
      }
      items.push(prevMsgItems[msg.id].item);
    });

    d.data.tasks.forEach((tpl) => {
      const prevItem = prevTaskItems[tpl.id];
      const options = [
        {
          key: 'duplicateTimelineItemTask',
          label: 'Duplicate',
          onClick: () => {
            // BG-check resources are cloned (not identity-mapped) so the
            // duplicate's package selection is independent of the original.
            // Closes main-378. Other storages stay identity-mapped — see ch9729.
            const { resourceIdToNewId, clonedBgCheckResources } =
              prepareTaskDuplicationResourceMap(tpl, d.data.resources);
            if (clonedBgCheckResources.length) {
              onChangeResources([
                ...d.data.resources,
                ...clonedBgCheckResources
              ]);
            }
            const duplicatedTask = duplicateTaskTemplate(
              tpl,
              resourceIdToNewId
            );
            setDuplicatedTask({
              ...duplicatedTask,
              title: `Copy of ${duplicatedTask.title}`
            });
            trackDuplicateButtonClicked(
              duplicatedTask.id,
              tpl.id,
              tpl.title,
              'Task'
            );
          }
        }
      ];

      // TODO Triggered items - these optimizations are a bit on the way of dealing properly with triggering and triggered
      // items. Once we also make triggered items moveable this will need more changes.
      if (
        forceNew ||
        prevItem?.tpl !== tpl ||
        isEventDrivenTiming(tpl.timing)
      ) {
        const causeItems = triggerHierarchyByEffects.tasks[tpl.id] || [];
        const { start, end } = getTimelinePositionForTaskTemplate(
          tpl.timing,
          d.data.tasks
        );
        const nextItem: TimelineItem = {
          key: tpl.id,
          data: { type: 'TASK', actor: tpl.actor, batchMode: tpl.batchMode },
          start,
          end,
          resizable: tpl.timing.type === 'fixed',
          movable: tpl.timing.type === 'fixed',
          parents: causeItems,
          body: (
            <TaskTimelineItem
              title={interpolateExperienceText(getItemDisplayName(tpl), {
                experience: { vars: { title: d.data.title, tasks: {} } },
                org: { vars: { name: org.name } },
                roles: []
              }, { dateFormat: 'YYYY/MM/DD' })}
              channels={tpl.channels}
              actor={actorDescriptors[tpl.actor]}
              deactivated={tpl.deactivated}
              onClick={() => onTaskClick(tpl.id)}
              connectUp={connectUpIds.has(tpl.id)}
              connectDown={connectDownIds.has(tpl.id)}
              connectLeft={triggerConnectors[tpl.id]?.left}
              connectRight={triggerConnectors[tpl.id]?.right}
              selectMenu={
                <AdditionalActionsMenu options={options} variant="horizontal" />
              }
            />
          )
        };
        prevTaskItems[tpl.id] = {
          tpl,
          item: nextItem
        };
      }
      items.push(prevTaskItems[tpl.id].item);
    });

    d.data.automations.forEach((tpl) => {
      const options = [
        {
          key: 'duplicateTimelineItemAutomation',
          label: 'Duplicate',
          onClick: () => {
            const duplicate = duplicateAutomationTemplate(tpl);
            setDuplicatedAutomation({
              ...duplicate,
              title: `Copy of ${duplicate.title}`
            });
            trackDuplicateButtonClicked(
              duplicate.id,
              tpl.id,
              tpl.title,
              'Automation'
            );
          }
        }
      ];

      // TODO Triggered items - these optimizations are a bit on the way of dealing properly with triggering and triggered
      // items. Once we also make triggered items moveable this will need more changes.
      if (
        forceNew ||
        prevAutomationItems[tpl.id]?.tpl !== tpl ||
        isEventDrivenTiming(tpl.timing)
      ) {
        const causeItems = triggerHierarchyByEffects.automations[tpl.id] || [];
        const { start, end } = getTimelinePositionForAutomationTemplate(
          tpl.timing,
          d.data.tasks
        );
        const nextItem: TimelineItem = {
          key: tpl.id,
          data: { type: 'AUTOMATION' },
          start,
          end,
          movable: true,
          parents: causeItems,
          body: (
            <AutomationTimelineItem
              tpl={tpl}
              onClick={() => onAutomationClick(tpl.id)}
              selectMenu={
                <AdditionalActionsMenu options={options} variant="horizontal" />
              }
              connectLeft={triggerConnectors[tpl.id]?.left}
              connectRight={triggerConnectors[tpl.id]?.right}
            />
          )
        };
        prevAutomationItems[tpl.id] = {
          tpl,
          item: nextItem
        };
      }
      items.push(prevAutomationItems[tpl.id].item);
    });

    return sortTaskTimelineItems(items);
  }, [
    d,
    actorDescriptors,
    onAutomationClick,
    onTaskClick,
    onMessageClick,
    setDuplicatedAutomation,
    setDuplicatedMessage,
    setDuplicatedTask,
    trackEvent
  ]);
};

const CreateHintContainer = styled('div')((p) => ({
  margin: p.theme.spacing(2)
}));

const CreateHintBubble = styled('div')((p) => ({
  width: p.theme.spacing(3),
  height: p.theme.spacing(3),
  borderRadius: '50%',
  backgroundColor: p.theme.custom.colors.success.main
}));

const StepTimelineContainer = styled('div')((p) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(3)
}));

const createEmptyMessage = (
  actor: string,
  subject: string,
  sendAt: number,
  preferUserTimezone: boolean
): ExperienceTemplateMessage => {
  const emptyBlock: ContentTemplate = {
    id: shortid(),
    parentLayoutElement: null,
    type: ContentType.TEXT,
    data: {
      t: {
        t: '',
        v: 1
      }
    }
  };

  return {
    id: shortid(),
    batchMode: BatchMode.DEFAULT,
    catchup: { disabled: false },
    actor,
    subject,
    body: {
      t: '',
      v: 1
    },
    blocks: [emptyBlock],
    audiences: [
      {
        id: shortid(),
        name: '',
        color: '',
        rule: 'EVERYONE',
        blocks: [emptyBlock.id]
      }
    ],
    channels: [
      {
        type: ScheduleChannelType.EMAIL,
        emailFrom: 'email'
      }
    ],
    sendAt,
    startTime: null,
    timing: {
      type: 'fixed',
      start: sendAt,
      time: null
    },
    preferUserTimezone,
    taskList: { range: 'ALL', variant: 'expanded' }
  };
};

export const StepTimeline = ({
  d,
  label,
  onChange,
  goTo,
  nextStep,
  onCheckChanges,
  onLaunchFromRoughDraft,
  isLaunchReady,
  onLaunch,
  lastSavedAt,
  validationErrors: allValidationErrors
}: StepBodyProps<Doc<IExperienceTemplate>>) => {
  console.log('TPL', d);
  const isRoughDraft =
    d.data.status === ExperienceTemplateStatus.ROUGH_DRAFT;
  const { ROUTES, goTo: goToRoute } = useRoutes();
  const { startTimer, getDuration } = useTrackDuration();
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { org } = useCurrentUser();
  const newUxEnabled = useNewUx().enabled;
  const anchorFields = useAnchorFields(org.customUserFields);
  const [createParams, setCreateParams] = useState<{
    start?: number;
    end?: number;
  } | null>(null);
  const createDialogOpen = !!createParams;
  const actorDescriptors = useActorDescriptorsFromTemplate(
    d.data.supportingActors
  );
  const [selectedActors, setSelectedActors] = useState<Set<string>>(new Set());
  // "Filter by Segment" — single-select and mutually exclusive with the actor
  // filter (each resets the other). See TaskTimeline for the same wiring.
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const { segments: templateSegments } = useTemplateSegments(d.data);
  const selectedSegment = useMemo(
    () => templateSegments.find((s) => s.id === selectedSegmentId) || null,
    [templateSegments, selectedSegmentId]
  );
  const segmentVisibleIds = useMemo(
    () =>
      selectedSegment
        ? getVisibleItemIdsForSegment(
            getTemplateAudienceCarriers(d.data),
            selectedSegment.rule
          )
        : null,
    [selectedSegment, d.data]
  );
  const onSegmentFilterChange = useCallback((nextId: string | null) => {
    // The two rows never combine: picking a segment restores every actor.
    setSelectedActors(new Set());
    setSelectedSegmentId(nextId);
  }, []);

  const [editedTaskId, setEditedTaskId] = useStringQueryParam('t', '');
  // Block/action to open in the edit dialog, set by the read-only dialogs'
  // Edit button. Cleared together with the item id when the dialog closes.
  const [editedBlockId, setEditedBlockId] = useStringQueryParam(
    SELECTED_BLOCK_QUERY_PARAM,
    ''
  );
  const [duplicatedTask, setDuplicatedTask] = useState<ITaskTemplate>();
  const editedTask = useMemo(
    () => d.data.tasks.find((tsk) => tsk.id === editedTaskId),
    [editedTaskId, d]
  );
  const closeTaskEditDialog = useCallback(() => {
    setEditedTaskId('');
    setEditedBlockId('');
  }, [setEditedTaskId, setEditedBlockId]);

  const [editedAutomationId, setEditedAutomationId] = useStringQueryParam(
    'a',
    ''
  );
  const [
    duplicatedAutomation,
    setDuplicatedAutomation
  ] = useState<IAutomationTemplate>();
  const editedAutomation = useMemo(
    () => d.data.automations.find((tsk) => tsk.id === editedAutomationId),
    [editedAutomationId, d]
  );
  const closeAutomationEditDialog = useCallback(() => {
    setEditedAutomationId('');
    setEditedBlockId('');
  }, [setEditedAutomationId, setEditedBlockId]);

  const [editedMessageId, setEditedMessageId] = useStringQueryParam('m', '');
  const [
    duplicatedMessage,
    setDuplicatedMessage
  ] = useState<ExperienceTemplateMessage>();
  const editedMessage = useMemo(
    () => d.data.messages.other.find((msg) => msg.id === editedMessageId),
    [editedMessageId, d]
  );
  const closeMessageEditDialog = useCallback(() => {
    setEditedMessageId('');
    setEditedBlockId('');
  }, [setEditedMessageId, setEditedBlockId]);

  const onGoToOtherItem = useCallback(
    (otherItem: ITimelineItem) => {
      if (otherItem.itemType === 'AUTOMATION') {
        setEditedAutomationId(otherItem.item.id);
      }
      if (otherItem.itemType === 'MSG') {
        setEditedMessageId(otherItem.item.id);
      }
      if (otherItem.itemType === 'TASK') {
        setEditedTaskId(otherItem.item.id);
      }
    },
    [setEditedTaskId, setEditedMessageId, setEditedAutomationId]
  );

  const createDialog = useMemo(() => {
    return {
      openDialog: (params: { start?: number; end?: number } | null) =>
        setCreateParams(params || null),
      closeDialog: () => setCreateParams(null)
    };
  }, []);

  useEffect(() => {
    if (d.data.messages.other !== null) {
      return;
    }
    const { anchor } = d.data;

    const otherMessages: ExperienceTemplateMessage[] =
      anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
      anchor.offset.type === 'ABSOLUTE' &&
      anchor.offset.duration < 0
        ? [
            createEmptyMessage(
              PARTICIPANT_ACTOR_KEY,
              '{{ experience.title }} continues!',
              Math.abs(anchor.offset.duration),
              d.data.preferUserTimezone
            )
          ]
        : [];

    onChange((x) =>
      updateMessages(x, { ...x.data.messages, other: otherMessages })
    );
  }, [d, onChange]);

  const {
    onMove,
    onTaskAdd,
    onTaskUpdate,
    onTaskDelete,
    onMessageAdd,
    onMessageUpdate,
    onMessageDelete,

    onChangeResources,
    onChangeSupportingActors,

    onAutomationAdd,
    onAutomationUpdate,
    onAutomationDelete
  } = useMemo(() => {
    return {
      onMove: (
        key: string,
        positions: {
          next: { start: number; end: number };
          prev: { start: number; end: number };
          diff: { start: number; end: number };
        },
        data: { type: 'TASK' | 'AUTOMATION' | 'MESSAGE' }
      ) => {
        const { start, end } = positions.next;
        console.log('MOVE', start, end);
        onChange((m) => {
          if (data.type === 'TASK') {
            const i = m.data.tasks.findIndex((x) => x.id === key);
            if (i === -1) {
              console.warn('Task not found', m.data.tasks, key);
              return m;
            }
            const nextTasks = m.data.tasks.slice();
            const nextTask: ITaskTemplate = Object.assign({}, nextTasks[i]);
            if (nextTask.timing.type === 'fixed') {
              nextTask.timing.start = start;
              nextTask.timing.end = end;
              nextTasks[i] = nextTask;
              return updateTasks(m, nextTasks);
            } else {
              // TODO Triggered Items -  cannot be moved yet
              return m;
            }
          }
          if (data.type === 'AUTOMATION') {
            const i = m.data.automations.findIndex((x) => x.id === key);
            if (i === -1) {
              console.warn('Automation not found', m.data.automations, key);
              return m;
            }
            const nextAutomations = m.data.automations.slice();
            const nextAutomation: IAutomationTemplate = Object.assign(
              {},
              nextAutomations[i]
            );
            if (nextAutomation.timing.type === 'fixed') {
              nextAutomation.timing.start = start;
              nextAutomations[i] = nextAutomation;
              return updateAutomations(m, nextAutomations);
            } else {
              // TODO Triggered Items -  cannot be moved yet
              return m;
            }
          }

          if (data.type === 'MESSAGE') {
            // dates of intro and outro cannot be changed, we can therefore ignore them here
            const messages = m.data.messages.other || [];
            const i = messages.findIndex((x) => x.id === key);
            if (i === -1) {
              console.warn('Message not found', m.data.messages, key);
              return m;
            }
            const nextMsgs = messages.slice();
            const nextMsg: ExperienceTemplateMessage = Object.assign(
              {},
              nextMsgs[i]
            );
            if (nextMsg.timing.type === 'fixed') {
              nextMsg.timing.start = start;
              nextMsgs[i] = nextMsg;
              return updateMessages(m, { ...m.data.messages, other: nextMsgs });
            } else {
              // TODO Triggered Items -  cannot be moved yet
              return m;
            }
          }

          console.log('UNKNOWN MOVE TYPE', data);
          return m;
        });
      },
      onTaskAdd: (task: ITaskTemplate) =>
        onChange((x) => updateTasks(x, [...x.data.tasks, task])),
      onTaskUpdate: (next: ITaskTemplate) =>
        onChange((m) => {
          const i = m.data.tasks.findIndex((x) => x.id === next.id);
          if (i === -1) {
            console.warn('Task not found', m.data.tasks, next);
            return m;
          }

          const taskToSave = Object.assign({}, next);
          if (taskToSave.blocks.some((b: any) => b.aiFeatureUsed)) {
            taskToSave.aiFeatureUsed = true;
          }

          const nextTasks = [...m.data.tasks];
          nextTasks[i] = taskToSave;

          const result = updateTasks(m, nextTasks);

          const messages = m.data.messages.other || [];
          if (
            nextTasks.some((t) => t.aiFeatureUsed) ||
            messages.some((msg) => msg.aiFeatureUsed)
          ) {
            result.data.aiFeatureUsed = true;
          }

          return result;
        }),

      onTaskDelete: (toDel: ITaskTemplate) =>
        onChange((m) => {
          const i = m.data.tasks.findIndex((x) => x.id === toDel.id);
          if (i === -1) {
            console.warn('Task not found', m.data.tasks, toDel);
            return m;
          }
          const nextTasks = [...m.data.tasks];
          nextTasks.splice(i, 1);
          const toDelEndAsNumber: number =
            toDel.timing.type === 'fixed'
              ? toDel.timing.end
              : toDel.timing.type === 'dataChange'
              ? (toDel.timing.earliestStart ?? 0) + toDel.timing.end.duration
              : 0;
          const triggeredItems = getTriggeredItemsForTask(
            m,
            toDel.id
          ).map<ITimelineItem>((x) => {
            // If the item itself is also triggered, then take over the trigger settings
            // of the deleted item
            if (x.itemType === 'MSG') {
              if (toDel.timing.type === 'triggered') {
                return {
                  ...x,
                  item: {
                    ...x.item,
                    timing: {
                      type: 'triggered',
                      dependentOn: toDel.timing.dependentOn
                    }
                  }
                };
              } else {
                return {
                  ...x,
                  item: {
                    ...x.item,
                    timing: {
                      type: 'fixed',
                      start: toDelEndAsNumber,
                      time: toDel.timing.time
                    }
                  }
                };
              }
            }
            if (x.itemType === 'AUTOMATION') {
              if (toDel.timing.type === 'triggered') {
                return {
                  ...x,
                  item: {
                    ...x.item,
                    timing: {
                      type: 'triggered',
                      dependentOn: toDel.timing.dependentOn
                    }
                  }
                };
              } else {
                return {
                  ...x,
                  item: {
                    ...x.item,
                    timing: {
                      type: 'fixed',
                      start: toDelEndAsNumber,
                      time: toDel.timing.time
                    }
                  }
                };
              }
            }
            if (x.itemType === 'TASK') {
              if (toDel.timing.type === 'triggered') {
                return {
                  ...x,
                  item: {
                    ...x.item,
                    timing: {
                      type: 'triggered',
                      dependentOn: toDel.timing.dependentOn,
                      end: toDel.timing.end
                    }
                  }
                };
              } else {
                return {
                  ...x,
                  item: {
                    ...x.item,
                    timing: {
                      type: 'fixed',
                      start: toDelEndAsNumber,
                      end:
                        toDelEndAsNumber +
                        (isEventDrivenTiming(x.item.timing)
                          ? x.item.timing.end.duration
                          : // This case can never happen
                            ONE_DAY * 5),
                      time: toDel.timing.time
                    }
                  }
                };
              }
            }
            return x;
          });
          if (triggeredItems.length) {
            return updateDataInDoc(m, {
              tasks: nextTasks.map((x) => {
                for (const item of triggeredItems) {
                  if (item.itemType === 'TASK' && x.id === item.item.id) {
                    return item.item;
                  }
                }
                return x;
              }),
              messages: {
                ...m.data.messages,
                other: m.data.messages.other.map((x) => {
                  for (const item of triggeredItems) {
                    if (item.itemType === 'MSG' && x.id === item.item.id) {
                      return item.item;
                    }
                  }
                  return x;
                })
              },
              automations: m.data.automations.map((x) => {
                for (const item of triggeredItems) {
                  if (item.itemType === 'AUTOMATION' && x.id === item.item.id) {
                    return item.item;
                  }
                }
                return x;
              })
            });
          } else {
            // just a simpler path that doesn't rewrite everything
            return updateTasks(m, nextTasks);
          }
        }),

      onMessageAdd: (msg: ExperienceTemplateMessage) =>
        onChange((m) =>
          updateMessages(m, {
            ...m.data.messages,
            other: [...(m.data.messages.other || []), msg]
          })
        ),
      onMessageUpdate: (msg: ExperienceTemplateMessage) => {
        onChange((m) => {
          const messages = m.data.messages.other || [];
          const i = messages.findIndex((x) => x.id === msg.id);
          if (i === -1) {
            console.warn('Message not found', m.data.messages, msg.id);
            return m;
          }

          const msgToSave = Object.assign({}, msg);
          if (msgToSave.blocks.some((b: any) => b.aiFeatureUsed)) {
            msgToSave.aiFeatureUsed = true;
          }

          const nextMessages = [...messages];
          nextMessages[i] = msgToSave;

          const result = updateMessages(m, {
            ...m.data.messages,
            other: nextMessages
          });

          if (
            m.data.tasks.some((t) => t.aiFeatureUsed) ||
            nextMessages.some((x) => x.aiFeatureUsed)
          ) {
            result.data.aiFeatureUsed = true;
          }

          return result;
        });
      },
      onMessageDelete: (msg: ExperienceTemplateMessage) => {
        // ignore intro and outro for now
        onChange((m) => {
          const messages = m.data.messages.other || [];
          const i = messages.findIndex((x) => x.id === msg.id);
          if (i === -1) {
            console.warn('Message not found', m.data.messages, msg.id);
            return m;
          }
          const nextMessages = [...messages];
          nextMessages.splice(i, 1);
          return updateMessages(m, { ...m.data.messages, other: nextMessages });
        });
      },
      onChangeResources: (rs: IExperienceTemplateResource[]) =>
        onChange((x) => updateResources(x, rs)),

      onChangeSupportingActors: (
        nextActors: {
          [key: string]: ISupportingActor;
        },
        propagationScope?: {
          actorKey: string;
          scope: SupportingActorPropagationScope;
        }
      ) =>
        onChange((x) =>
          updateSupportingActors(x, nextActors, propagationScope)
        ),

      onAutomationAdd: (automation: IAutomationTemplate) =>
        onChange((x) =>
          updateAutomations(x, [...x.data.automations, automation])
        ),
      onAutomationUpdate: (next: IAutomationTemplate) =>
        onChange((m) => {
          const i = m.data.automations.findIndex((x) => x.id === next.id);
          if (i === -1) {
            console.warn('Autoation not found', m.data.automations, next);
            return m;
          }
          const nextAutomations = [...m.data.automations];
          nextAutomations[i] = next;
          return updateAutomations(m, nextAutomations);
        }),
      onAutomationDelete: (toDel: IAutomationTemplate) =>
        onChange((m) => {
          const i = m.data.automations.findIndex((x) => x.id === toDel.id);
          if (i === -1) {
            console.warn('Automation not found', m.data.automations, toDel);
            return m;
          }
          const nextAutomations = [...m.data.automations];
          nextAutomations.splice(i, 1);
          return updateAutomations(m, nextAutomations);
        })
    };
  }, [onChange]);

  /*
   * We deliberately create an entire new URL, as appending query parameters (via changeQuery, which
   * is used by e.g. setEditedTaskId) results in multiple timeline item type query params (e.g. m=:id&a=:id).
   * See: https://app.shortcut.com/emplexp/story/3331/using-the-edit-button-in-view-modals-makes-the-editor-modal-for-that-particular-task-message-automation-keep-opening-in-edit
   */
  const openAutomation = useCallback(
    (automationId: string) =>
      goToRoute(
        ROUTES.experiences.details.edit.url(d.id, {
          step: 'tasks',
          automationId
        })
      ),
    [ROUTES.experiences.details.edit, d.id, goToRoute]
  );
  const openTask = useCallback(
    (taskId: string) =>
      goToRoute(
        ROUTES.experiences.details.edit.url(d.id, { step: 'tasks', taskId })
      ),
    [ROUTES.experiences.details.edit, d.id, goToRoute]
  );
  const openMessage = useCallback(
    (messageId: string) =>
      goToRoute(
        ROUTES.experiences.details.edit.url(d.id, { step: 'tasks', messageId })
      ),
    [ROUTES.experiences.details.edit, d.id, goToRoute]
  );

  const items = useTimelineItems(
    d,
    actorDescriptors,
    openTask,
    openAutomation,
    openMessage,
    setDuplicatedTask,
    setDuplicatedAutomation,
    setDuplicatedMessage,
    onChangeResources
  );
  const durationInDays = items.length
    ? Math.max(...items.map((i) => i.end)) / ONE_DAY + 1
    : 0;

  const { anchor } = d.data;

  const [scale, setScale] = useState(1);
  const onScaleChangeRequest = useTaskTimelineScaleChangeRequest(setScale);

  const expectedEnd = items.length
    ? Math.max(
        ...items.map((i) => {
          if (i.data?.type === 'TASK') {
            return i.end;
          } else {
            return i.start;
          }
        })
      ) + d.data.expectedEndThreshold
    : 0;

  const anchorRulers = useAllRelevantAnchorRulers(
    anchor,
    anchorFields,
    t,
    expectedEnd,
    items.length > 0
  );

  const orgDateFormat = org.dateFormats.MMDDYYYY;
  const { tz } = d.data;
  // Stable so the memoised header doesn't re-render on unrelated state changes.
  const renderOuterUnit = useCallback(
    (i: number, gridOffset: number) =>
      newUxEnabled
        ? getWeekLabel(
            anchor,
            tz,
            orgDateFormat,
            i * 7 + Math.round(gridOffset / ONE_DAY),
            t
          ).full
        : `${t('Week')} ${i + 1}`,
    [newUxEnabled, anchor, tz, orgDateFormat, t]
  );

  const expTplMini = useTaskBuilderContext(d.data);

  const hasAutomation = items.some((a) => a.data?.type === 'AUTOMATION');

  const filteredItems = useMemo(() => {
    if (segmentVisibleIds) {
      return items.filter((x) => segmentVisibleIds.has(x.key));
    }
    if (!selectedActors.size) {
      return items;
    }
    return items.filter((x) => {
      if (x.data?.type === 'AUTOMATION') {
        return selectedActors.has('AUTOMATION');
      }
      return selectedActors.has(x.data?.actor);
    });
  }, [items, selectedActors, segmentVisibleIds]);

  return (
    <StepTimelineContainer>
      <Grid container justify="space-between">
        <Grid item xs>
          <Typography component="h1" variant="h3">
            {label}
          </Typography>
        </Grid>
        <Grid item>
          <FlexContainer>
            <ExperiencePreviewV2Button
              startIcon={<Eye size={16} />}
              variant="outlined"
              color="primary"
              d={d}
            >
              {t('Preview experience')}
            </ExperiencePreviewV2Button>

            <TemplateSearchButton
              tpl={d}
              variant="contained"
              color="primary"
              onSelectMatch={(item) => {
                if (item.type === 'MSG') {
                  openMessage(item.item.id);
                }
                if (item.type === 'AUTOMATION') {
                  openAutomation(item.item.id);
                }
                if (item.type === 'TASK') {
                  openTask(item.item.id);
                }
              }}
            />
          </FlexContainer>
        </Grid>
      </Grid>
      <FlexContainer alignItems="flex-start" direction="column" spacing={1}>
        <FlexContainer spacing={1}>
          <Typography variant="h6" color="textSecondary">
            {t('Filter by Actor')}
          </Typography>
          <TemplateManageActorsButton
            tpl={d}
            onAdd={(nextActor) =>
              onChangeSupportingActors({
                ...d.data.supportingActors,
                [nextActor.key]: nextActor
              })
            }
            onEdit={(nextActor, editedActor, propagationScope) =>
              onChangeSupportingActors(
                {
                  ...omit(d.data.supportingActors, [editedActor]),
                  [nextActor.key]: nextActor
                },
                propagationScope && {
                  actorKey: nextActor.key,
                  scope: propagationScope
                }
              )
            }
            onRemove={(removedActor) =>
              onChangeSupportingActors(
                omit(d.data.supportingActors, removedActor)
              )
            }
            onArchive={(archivedActor) =>
              onChangeSupportingActors({
                ...d.data.supportingActors,
                [archivedActor]: {
                  ...d.data.supportingActors[archivedActor],
                  archived: true
                } as ISupportingActor
              })
            }
            onUnarchive={(unarchivedActor) =>
              onChangeSupportingActors({
                ...d.data.supportingActors,
                [unarchivedActor]: {
                  ...d.data.supportingActors[unarchivedActor],
                  archived: false
                } as ISupportingActor
              })
            }
          />
        </FlexContainer>
        <FlexContainer spacing={1}>
          <ActorLegendV2
            descriptors={actorDescriptors}
            selected={selectedActors}
            setSelected={(nextFilter) => {
              // The two rows never combine: touching the actor filter clears
              // the segment filter.
              setSelectedSegmentId(null);
              // const [selected, deselected] = partition(
              //   actors,
              //   (p) => !nextFilter.has(p.key)
              // );
              // trackFilterChange(selected, deselected);
              setSelectedActors(nextFilter);
            }}
            hasAutomation={hasAutomation}
          />
        </FlexContainer>
      </FlexContainer>
      <SegmentFilterRow
        segments={templateSegments}
        selectedId={selectedSegmentId}
        onSelect={onSegmentFilterChange}
      />
      <TimelineContainer>
        <Timeline
          items={filteredItems}
          innerUnitSpan={ONE_DAY * 1}
          outerUnitSpan={ONE_DAY * 7}
          minUnits={Math.max(
            ONE_DAY * 7 * 4,
            (Math.floor(durationInDays / 7) + 2) * 7 * ONE_DAY
          )}
          minRows={1}
          renderOuterUnit={renderOuterUnit}
          onMove={onMove}
          rulers={anchorRulers}
          emptyOverlay={
            !createDialogOpen && (
              <CreateHintContainer>
                <Tooltip
                  placement="right"
                  arrow
                  title="Click and drag here to create a new task"
                  open={true}
                  classes={{
                    arrow: css({
                      color: `${theme.custom.colors.success.main} !important`
                    }),
                    tooltip: css({
                      background: `${theme.custom.colors.success.main} !important`,
                      color: `${theme.custom.colors.success.contrastText} !important`,
                      fontSize: `${theme.custom.fontSize.s}px !important`,
                      border: `1px solid ${theme.custom.colors.success.main} !important`
                    }),
                    popper: css({
                      '&&': { zIndex: 1000 } // defaults to 1500, which is more than modals with 1300. We want that modals show on top
                    })
                  }}
                >
                  <CreateHintBubble />
                </Tooltip>
              </CreateHintContainer>
            )
          }
          create={{
            renderPlaceholder: () => (
              <TaskTimelineItem
                title=""
                actor={actorDescriptors[PARTICIPANT_ACTOR_KEY]}
                channels={[
                  { type: ScheduleChannelType.EMAIL, emailFrom: 'email' }
                ]}
                variant="placeholder"
              />
            ),
            defaultDuration: 5 * ONE_DAY,
            onCreate: (params) => createDialog.openDialog(params)
          }}
          scale={scale}
          onScaleChangeRequest={onScaleChangeRequest}
        />
        <TaskTimelineZoomControlsContainer stickyOffset={100}>
          <TaskTimelineZoomControls value={scale} onChange={setScale} />
        </TaskTimelineZoomControlsContainer>
      </TimelineContainer>

      <StandardFooter
        d={d}
        onCheckChanges={onCheckChanges}
        onLaunchFromRoughDraft={onLaunchFromRoughDraft}
        isLaunchReady={isLaunchReady}
        onLaunch={onLaunch}
        lastSavedAt={lastSavedAt}
      />

      <ComboAddDialog
        open={createDialogOpen}
        onClose={createDialog.closeDialog}
        onTaskAdd={onTaskAdd}
        onAutomationAdd={onAutomationAdd}
        onMessageAdd={onMessageAdd}
        expTpl={d}
        resources={d.data.resources}
        onChangeResources={onChangeResources}
        params={createParams || undefined}
        customFields={org.customUserFields}
        anchorFields={anchorFields}
        onGoToOtherItem={onGoToOtherItem}
        isRoughDraft={isRoughDraft}
      />

      {editedTask && (
        <TaskEditDialog
          key={editedTask.id}
          open={true}
          onClose={closeTaskEditDialog}
          tpl={editedTask}
          initialSelectedBlockId={
            editedTask.blocks.some((b) => b.id === editedBlockId)
              ? editedBlockId
              : null
          }
          expTpl={d}
          resources={d.data.resources}
          onUpdate={onTaskUpdate}
          onDelete={onTaskDelete}
          onChangeResources={onChangeResources}
          setDuplicatedTask={setDuplicatedTask}
          onGoToOtherItem={onGoToOtherItem}
          isRoughDraft={isRoughDraft}
          validationErrors={allValidationErrors?.find((e) => e.itemId === editedTask.id)?.errors}
        />
      )}
      {duplicatedTask && (
        <TaskAddDialog
          key={duplicatedTask.id}
          open={true}
          onClose={() => setDuplicatedTask(undefined)}
          onAdd={onTaskAdd}
          expTpl={d}
          resources={d.data.resources}
          onChangeResources={onChangeResources}
          duplicatedTask={duplicatedTask}
          startTimer={startTimer}
          getDuration={getDuration}
          onGoToOtherItem={onGoToOtherItem}
          isRoughDraft={isRoughDraft}
        />
      )}
      {editedAutomation && (
        <>
        {allValidationErrors?.find((e) => e.itemId === editedAutomation.id) && (
          <div style={{ position: 'fixed', top: 64, left: 0, right: 0, zIndex: 1301 }}>
            <ValidationBanner errors={allValidationErrors.find((e) => e.itemId === editedAutomation.id)!.errors} />
          </div>
        )}
        <AutomationEditDialog
          key={editedAutomation.id}
          open={true}
          onClose={closeAutomationEditDialog}
          value={editedAutomation}
          initialSelectedActionId={
            editedAutomation.actions.some((a) => a.id === editedBlockId)
              ? editedBlockId
              : null
          }
          onChange={onAutomationUpdate}
          resources={d.data.resources}
          onChangeResources={onChangeResources}
          onDelete={onAutomationDelete}
          expTpl={expTplMini}
          customFields={org.customUserFields}
          anchorFields={anchorFields}
          setDuplicatedAutomation={setDuplicatedAutomation}
          expTplId={d.id}
          onGoToOtherItem={onGoToOtherItem}
          isRoughDraft={isRoughDraft}
        />
        </>
      )}

      {duplicatedAutomation && (
        <AutomationAddDialog
          open={true}
          onClose={() => setDuplicatedAutomation(undefined)}
          onAdd={onAutomationAdd}
          resources={d.data.resources}
          onChangeResources={onChangeResources}
          expTpl={expTplMini}
          customFields={org.customUserFields}
          anchorFields={anchorFields}
          duplicatedAutomation={duplicatedAutomation}
          getDuration={getDuration}
          startTimer={startTimer}
          onGoToOtherItem={onGoToOtherItem}
          isRoughDraft={isRoughDraft}
        />
      )}
      {editedMessage && (
        <>
        {allValidationErrors?.find((e) => e.itemId === editedMessage.id) && (
          <div style={{ position: 'fixed', top: 64, left: 0, right: 0, zIndex: 1301 }}>
            <ValidationBanner errors={allValidationErrors.find((e) => e.itemId === editedMessage.id)!.errors} />
          </div>
        )}
        <MessageTemplateDialog
          key={editedMessage.id}
          open={true}
          onClose={closeMessageEditDialog}
          value={editedMessage}
          initialSelectedBlockId={
            editedMessage.blocks.some((b) => b.id === editedBlockId)
              ? editedBlockId
              : null
          }
          onChange={onMessageUpdate}
          onDelete={onMessageDelete}
          expTpl={d}
          mode="edit"
          setDuplicatedMessage={setDuplicatedMessage}
          startTimer={startTimer}
          getDuration={getDuration}
          onGoToOtherItem={onGoToOtherItem}
          isRoughDraft={isRoughDraft}
        />
        </>
      )}

      {duplicatedMessage && (
        <MessageTemplateDialog
          key={duplicatedMessage.id}
          open={true}
          onClose={() => setDuplicatedMessage(undefined)}
          value={duplicatedMessage}
          onChange={onMessageAdd}
          onDelete={onMessageDelete}
          expTpl={d}
          mode="duplicate"
          startTimer={startTimer}
          getDuration={getDuration}
          onGoToOtherItem={onGoToOtherItem}
          isRoughDraft={isRoughDraft}
        />
      )}
    </StepTimelineContainer>
  );
};

/*




*/
